import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { audioService } from '@/services/audioService';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import type { AudioTrack } from '@/types/audio';

interface AudioCatalogBrowserProps {
  selectedTrackId?: string;
  onSelect: (trackId: string | null) => void;
}

export function AudioCatalogBrowser({ selectedTrackId, onSelect }: AudioCatalogBrowserProps) {
  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);

  useEffect(() => {
    loadTracks();
  }, []);

  const loadTracks = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await audioService.browseCatalog();
      setTracks(response.tracks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el catálogo de audio');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlay = (trackId: string, audioElement: HTMLAudioElement | null) => {
    if (!audioElement) return;

    if (playingTrackId && playingTrackId !== trackId) {
      const prevAudio = document.getElementById(`audio-${playingTrackId}`) as HTMLAudioElement;
      if (prevAudio) {
        prevAudio.pause();
        prevAudio.currentTime = 0;
      }
    }

    if (audioElement.paused) {
      audioElement.play();
      setPlayingTrackId(trackId);
    } else {
      audioElement.pause();
      audioElement.currentTime = 0;
      setPlayingTrackId(null);
    }
  };

  const handleAudioEnded = () => {
    setPlayingTrackId(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm font-light text-gray-600">
          {tracks.length} {tracks.length === 1 ? 'pista disponible' : 'pistas disponibles'}
        </p>
        {selectedTrackId && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSelect(null)}
            className="text-xs font-light min-h-9"
          >
            Quitar música
          </Button>
        )}
      </div>

      {tracks.length === 0 ? (
        <div className="p-6 bg-gray-50 border border-gray-200 rounded-md text-center">
          <p className="text-sm font-light text-gray-500">No hay pistas de audio disponibles</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div
            className={cn(
              'border rounded-md p-3 cursor-pointer transition-all',
              !selectedTrackId
                ? 'border-gray-900 bg-gray-50'
                : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
            )}
            onClick={() => onSelect(null)}
          >
            <div className="flex justify-between items-center">
              <div>
                <span className="font-medium text-gray-900">Sin música de fondo</span>
                <p className="text-xs font-light text-gray-500 mt-0.5">El video será silencioso</p>
              </div>
              {!selectedTrackId && (
                <span className="text-xs font-medium text-gray-900 bg-gray-200 px-2 py-1 rounded">
                  Activo
                </span>
              )}
            </div>
          </div>

          {tracks.map(track => (
            <div
              key={track.id}
              className={cn(
                'border rounded-md p-3 cursor-pointer transition-all',
                selectedTrackId === track.id
                  ? 'border-gray-900 bg-gray-50'
                  : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
              )}
              onClick={() => onSelect(track.id)}
            >
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 truncate">{track.title}</span>
                    {selectedTrackId === track.id && (
                      <span className="text-xs font-medium text-gray-900 bg-gray-200 px-2 py-0.5 rounded flex-shrink-0">
                        Activa
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-light text-gray-600">{track.artist}</p>
                  <p className="text-xs font-light text-gray-500 mt-1">
                    {track.genre} • {track.mood} • {Math.round(track.duration)}s
                  </p>
                </div>

                {track.preview_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      const audio = document.getElementById(`audio-${track.id}`) as HTMLAudioElement;
                      handlePlay(track.id, audio);
                    }}
                    className="text-xs font-light min-h-9 flex-shrink-0"
                  >
                    {playingTrackId === track.id ? '⏸ Parar' : '▶ Escuchar'}
                  </Button>
                )}
              </div>

              {track.preview_url && (
                <audio
                  id={`audio-${track.id}`}
                  src={track.preview_url}
                  onEnded={handleAudioEnded}
                  className="hidden"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
